'use client';

import { Slot as RadixSlot } from 'radix-ui/slot';
import type { ReactNode } from 'react';
import { useImperativeHandle, useRef } from 'react';
import { isActiveTrigger, specifyModalReturnFocus, trackLastClickedTrigger } from './modalFocusCoordination';

export interface ControlledModalTriggerProps {
  /** Single child element that will receive the onClick handler */
  children: ReactNode;
  /** Whether the external modal is currently open */
  modalOpen: boolean;
  /**
   * Optional key to coordinate focus restoration with Modal's returnFocusKey prop.
   * Only needed when using programmatic modal opens (e.g., auto-open on mount).
   * For regular click-triggered modals, focus restoration works automatically.
   */
  returnFocusKey?: string;
  /** Optional ref forwarded to the trigger element */
  ref?: React.Ref<HTMLElement>;
  /** Optional onClick handler to merge with modal open behavior */
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
}

/**
 * A wrapper component for modal triggers when modal state lives in an external store.
 *
 * This component merges an onClick handler onto the child element using Radix Slot.
 * The child must be a single element that accepts onClick (like Button, IconButton, etc.).
 *
 * Focus automatically returns to the clicked trigger when the modal closes.
 * For programmatic modal opens, provide a returnFocusKey and use specifyModalReturnFocus().
 *
 * ### When to use
 * - Opening a `Modal` whose open state lives in an external store (Zustand, URL state) rather than in `Modal.Trigger`
 *
 * ### When NOT to use
 * - Local modal state: use `Modal.Trigger` inside `Modal`, which manages open state and focus return itself
 *
 * @example
 * Basic usage (click-triggered, automatic focus restoration):
 * ```tsx
 * <ControlledModalTrigger modalOpen={isOpen} onClick={() => setIsOpen(true)}>
 *   <Button>Open Modal</Button>
 * </ControlledModalTrigger>
 *
 * <Modal open={isOpen} onOpenChange={setIsOpen}>...</Modal>
 * ```
 *
 * @example
 * With programmatic open (requires returnFocusKey):
 * ```tsx
 * const buttonRef = useRef(null);
 *
 * useEffect(() => {
 *   if (shouldAutoOpen) {
 *     specifyModalReturnFocus('myModal', buttonRef);
 *     setIsOpen(true);
 *   }
 * }, [shouldAutoOpen]);
 *
 * <ControlledModalTrigger modalOpen={isOpen} onClick={() => setIsOpen(true)} returnFocusKey="myModal">
 *   <Button ref={buttonRef}>Open Modal</Button>
 * </ControlledModalTrigger>
 *
 * <Modal open={isOpen} onOpenChange={setIsOpen} returnFocusKey="myModal">...</Modal>
 * ```
 *
 * @headless Merges behaviour onto the child it wraps; the child keeps its own identity.
 */
export const ControlledModalTrigger: React.FC<ControlledModalTriggerProps> = ({
  children,
  modalOpen,
  returnFocusKey,
  ref,
  onClick,
  ...props
}) => {
  const triggerRef = useRef<HTMLElement>(null);

  // Expose the trigger ref to parent via forwarded ref
  useImperativeHandle(ref, () => triggerRef.current!, []);

  /**
   * Handles click on the trigger element. Sets up focus restoration and calls the onClick handler.
   * If a key is provided, use keyed coordination (for advanced use cases, e.g. programmatic opens).
   * Otherwise, rely on the tracked triggers (for simple click-triggered opens)
   */
  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    if (returnFocusKey) {
      specifyModalReturnFocus(returnFocusKey, triggerRef);
    }
    trackLastClickedTrigger(triggerRef);
    onClick?.(e);
  };

  // Only set aria-expanded to true if the modal is open AND this specific trigger opened it
  const isExpanded = modalOpen && isActiveTrigger(triggerRef, returnFocusKey);

  return (
    <RadixSlot ref={triggerRef} onClick={handleClick} aria-haspopup="dialog" aria-expanded={isExpanded} {...props}>
      {children}
    </RadixSlot>
  );
};
